/**
 * Select tool — rectangular selection on tile layers with copy/paste/delete.
 *
 * Copy (Ctrl+C): captures tile data from active layer within selection bounds.
 * Paste (Ctrl+V): enters paste mode — stamp follows cursor, click to place.
 * Delete: fills selection with 0 on active layer.
 */
export class SelectTool {
  constructor(state) {
    this.state = state;
    this._startX = 0;
    this._startY = 0;
    this._pasting = false;
  }

  onDown(tx, ty, e) {
    if (this._pasting) {
      this._placeClipboard(tx, ty);
      this._pasting = false;
      this.state.set('stamp', null);
      return;
    }

    // Shift+click = magic wand (select contiguous same-tile region)
    if (e && e.shiftKey) {
      this.magicWand(tx, ty);
      return;
    }

    this._startX = tx;
    this._startY = ty;
    this.state.set('selectionStart', { x: tx, y: ty });
    this.state.set('selectionEnd', { x: tx, y: ty });
  }

  onMove(tx, ty) {
    if (this._pasting) return;
    this.state.set('selectionEnd', { x: tx, y: ty });
  }

  onUp() {
    // Selection stays visible until next action
  }

  /** Copy tile data from the active layer within the current selection. */
  copySelection() {
    const start = this.state.get('selectionStart');
    const end = this.state.get('selectionEnd');
    if (!start || !end) return;

    const x0 = Math.min(start.x, end.x);
    const y0 = Math.min(start.y, end.y);
    const x1 = Math.max(start.x, end.x);
    const y1 = Math.max(start.y, end.y);
    const w = x1 - x0 + 1;
    const h = y1 - y0 + 1;

    const layerIndex = this.state.get('activeLayerIndex');
    const layers = this.state.get('layers');
    const layer = layers[layerIndex];
    if (!layer || !layer.data) return;

    const mapW = this.state.get('mapWidth');
    const data = [];
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        data.push(layer.data[(y0 + dy) * mapW + (x0 + dx)] || 0);
      }
    }

    this.state.set('clipboard', { width: w, height: h, data });
  }

  /** Enter paste mode — stamp follows cursor until click places it. */
  startPaste() {
    const clipboard = this.state.get('clipboard');
    if (!clipboard) return;

    this._pasting = true;

    // Build a 2D stamp array for CursorPreview to render
    const stamp = [];
    for (let y = 0; y < clipboard.height; y++) {
      const row = [];
      for (let x = 0; x < clipboard.width; x++) {
        row.push(clipboard.data[y * clipboard.width + x]);
      }
      stamp.push(row);
    }
    this.state.set('stamp', stamp);
  }

  /** Cancel an active paste operation. */
  cancelPaste() {
    if (this._pasting) {
      this._pasting = false;
      this.state.set('stamp', null);
    }
  }

  /** Check whether the tool is currently in paste mode. */
  isPasting() {
    return this._pasting;
  }

  /** Delete (fill with 0) the selected region on the active layer. */
  deleteSelection() {
    const start = this.state.get('selectionStart');
    const end = this.state.get('selectionEnd');
    if (!start || !end) return;

    const x0 = Math.min(start.x, end.x);
    const y0 = Math.min(start.y, end.y);
    const x1 = Math.max(start.x, end.x);
    const y1 = Math.max(start.y, end.y);

    const layerIndex = this.state.get('activeLayerIndex');
    const layers = this.state.get('layers');
    const layer = layers[layerIndex];
    if (!layer || !layer.data) return;

    const mapW = this.state.get('mapWidth');
    for (let dy = y0; dy <= y1; dy++) {
      for (let dx = x0; dx <= x1; dx++) {
        layer.data[dy * mapW + dx] = 0;
      }
    }

    this.state.set('dirty', true);
    // Trigger layer re-render
    this.state.set('layers', layers);
  }

  /** Place clipboard contents at the given tile position on the active layer. */
  _placeClipboard(tx, ty) {
    const clipboard = this.state.get('clipboard');
    if (!clipboard) return;

    const layerIndex = this.state.get('activeLayerIndex');
    const layers = this.state.get('layers');
    const layer = layers[layerIndex];
    if (!layer || !layer.data) return;

    const mapW = this.state.get('mapWidth');
    const mapH = this.state.get('mapHeight');

    for (let dy = 0; dy < clipboard.height; dy++) {
      for (let dx = 0; dx < clipboard.width; dx++) {
        const px = tx + dx;
        const py = ty + dy;
        if (px < 0 || py < 0 || px >= mapW || py >= mapH) continue;
        layer.data[py * mapW + px] = clipboard.data[dy * clipboard.width + dx];
      }
    }

    this.state.set('dirty', true);
    // Trigger layer re-render
    this.state.set('layers', layers);
  }

  /**
   * Magic wand: select all contiguous tiles of the same type starting from (tx, ty).
   * Sets selection bounds to the bounding box of the flood-filled area.
   */
  magicWand(tx, ty) {
    const layers = this.state.get('layers');
    const activeIdx = this.state.get('activeLayerIndex');
    const layer = layers[activeIdx];
    if (!layer || !layer.data) return;

    const mapW = this.state.get('mapWidth');
    const mapH = this.state.get('mapHeight');
    const targetGid = layer.data[ty * mapW + tx] & 0x1FFFFFFF; // Strip flip flags

    const visited = new Uint8Array(mapW * mapH);
    const stack = [{ x: tx, y: ty }];
    let minX = tx, maxX = tx, minY = ty, maxY = ty;

    while (stack.length > 0) {
      const { x, y } = stack.pop();
      if (x < 0 || y < 0 || x >= mapW || y >= mapH) continue;
      const idx = y * mapW + x;
      if (visited[idx]) continue;
      const gid = layer.data[idx] & 0x1FFFFFFF;
      if (gid !== targetGid) continue;

      visited[idx] = 1;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;

      stack.push({ x: x - 1, y }, { x: x + 1, y }, { x, y: y - 1 }, { x, y: y + 1 });
    }

    this.state.set('selectionStart', { x: minX, y: minY });
    this.state.set('selectionEnd', { x: maxX, y: maxY });
  }
}
